import { useMemo } from 'react';
import { FilterAggregations } from 'src/features/chat/types';
import { formatDocumentAuthorityLabel } from 'src/features/wiki/constants/documentAuthority';

export interface FilterOption {
  label: string;
  value: string;
  count: number;
  disabled?: boolean;
}

/**
 * Build filter options from server-provided aggregations.
 * All filtering is done server-side; this hook just transforms aggregations into UI options.
 */
export const useFilterOptions = (aggregations?: FilterAggregations) => {
  const filterOptions = useMemo(() => {
    const empty = {
      categories: [] as FilterOption[],
      domains: [] as FilterOption[],
      subDomains: [] as FilterOption[],
      authorities: [] as FilterOption[]
    };
    if (!aggregations) return empty;

    const createOptionsFromAggregations = (counts: Record<string, number>): FilterOption[] => {
      return Object.entries(counts)
        .map(([value, count]) => ({
          label: value,
          value,
          count,
          disabled: count === 0
        }))
        .sort((a, b) => {
          if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
          return a.label.localeCompare(b.label);
        });
    };

    return {
      categories: createOptionsFromAggregations(aggregations.category_counts),
      domains: createOptionsFromAggregations(aggregations.domain_counts),
      subDomains: createOptionsFromAggregations(aggregations.subdomain_counts),
      authorities: createOptionsFromAggregations(aggregations.authority_counts).map((opt) => ({
        ...opt,
        label: formatDocumentAuthorityLabel(opt.value)
      }))
    };
  }, [aggregations]);

  return filterOptions;
};
